import type { RoundResult, RoundSnapshotV3 } from "../round/round-types.js";
import type { ContestProgress } from "../contest/contest-result.js";
import { RoundResultError } from "../round/round-errors.js";

/** Same canonical contest contract, calculated from reviewed totals instead of invented holes. */
export function importedTotalsProgress(
  snapshot: RoundSnapshotV3,
): ContestProgress {
  const totals = snapshot.importedTotals;
  if (!totals) throw new RoundResultError("The reviewed totals are missing.");
  const contest = snapshot.mainContest;
  const stableford = contest.format === "stableford";
  const sideTotals = contest.sides.map((side) => {
    const player = totals.players[side.participantIds[0]!];
    if (!player)
      throw new RoundResultError("A player's imported total is missing.");
    const value = stableford
      ? player.stableford
      : contest.scoringBasis === "net"
        ? player.net
        : player.gross;
    if (value === null)
      throw new RoundResultError("The agreed result total is unknown.");
    return { sideId: side.id, value };
  });
  const sorted = sideTotals
    .map((side) => side.value)
    .sort((a, b) => (stableford ? b - a : a - b));
  const winnerSideIds = sideTotals
    .filter((side) => side.value === sorted[0])
    .map((side) => side.sideId);
  return {
    complete: true,
    result: {
      calculationVersion: "3",
      winnerSideIds,
      tied: winnerSideIds.length > 1,
      sideTotals,
      unit: stableford ? "pts" : "strokes",
      holesPlayed: totals.holeCount,
      decidedAtHole: null,
      leadSideId: winnerSideIds.length === 1 ? winnerSideIds[0]! : null,
      margin: sorted.length > 1 ? Math.abs(sorted[0]! - sorted[1]!) : 0,
      remainingHoles: 0,
      label: sideTotals.map((side) => side.value).join("–"),
      dormie: false,
      decisionReason: "completed",
    },
  };
}

export function importedRoundResult(snapshot: RoundSnapshotV3): RoundResult {
  const progress = importedTotalsProgress(snapshot);
  return {
    calculationVersion: "3",
    format: snapshot.mainContest.format,
    winnerIds: snapshot.mainContest.sides
      .filter((side) => progress.result.winnerSideIds.includes(side.id))
      .flatMap((side) => side.participantIds),
    tied: progress.result.tied,
    nassau: null,
    standings: snapshot.participants.map((player) => {
      const facts = snapshot.importedTotals!.players[player.id]!;
      return {
        targetId: player.id,
        gross: facts.gross,
        net: facts.net,
        stableford: facts.stableford,
        holesWon: null,
        metric: null,
      };
    }),
  };
}
